/* Shared SLA classification + UI helpers for the planner views.
 *
 * SLA bands (hours since the case last changed status):
 *   ok       → ≤24h
 *   warn     → >24h, ≤48h
 *   breach   → >48h
 *
 * Only "ball-in-planner-court" statuses count toward SLA — the planner
 * isn't penalised for cases sitting with the doctor.
 */
import React from "react";
import { Clock, ShieldWarning, CheckCircle } from "@phosphor-icons/react";

export const SLA_HOURS_WARN = 24;
export const SLA_HOURS_BREACH = 48;

export const SLA_OWNED_STATUSES = [
    "in_planning",
    "info_requested",
    "revision_requested",
    "refinement_requested",
];

export function hoursSince(iso) {
    if (!iso) return 0;
    return Math.max(0, (Date.now() - new Date(iso).getTime()) / 3600000);
}

export function slaBandOf(c) {
    if (!SLA_OWNED_STATUSES.includes(c?.status)) return "neutral";
    const h = hoursSince(c.status_updated_at || c.updated_at || c.created_at);
    if (h > SLA_HOURS_BREACH) return "breach";
    if (h > SLA_HOURS_WARN) return "warn";
    return "ok";
}

const PILL_TONES = {
    ok:      { bg: "bg-emerald-50",  text: "text-emerald-800",  border: "border-emerald-200",  Icon: CheckCircle,    label: "On-track" },
    warn:    { bg: "bg-amber-50",    text: "text-amber-800",    border: "border-amber-200",    Icon: Clock,          label: "At-risk" },
    breach:  { bg: "bg-rose-50",     text: "text-rose-800",     border: "border-rose-200",     Icon: ShieldWarning,  label: "Breached" },
    neutral: { bg: "bg-secondary",   text: "text-muted-foreground", border: "border-border",   Icon: Clock,          label: "" },
};

export function SLAPill({ c, compact = false, showHours = true }) {
    const band = slaBandOf(c);
    if (band === "neutral") return null;
    const t = PILL_TONES[band];
    const h = Math.round(hoursSince(c.status_updated_at || c.updated_at || c.created_at));
    return (
        <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-bold ${t.bg} ${t.text} ${t.border}`}
            data-testid={`sla-pill-${band}`}
            title={`${t.label} · ${h}h with you`}
        >
            <t.Icon size={10} weight="fill" />
            {showHours ? `${h}h` : t.label}
            {!compact && <span className="ml-0.5 opacity-70 hidden sm:inline">{t.label}</span>}
        </span>
    );
}

/** Compute breach + warn counts split by bucket. Returns
 *  { byBucket: {bucketId: {breach, warn, ok}}, total: {breach, warn} } */
export function slaCountsByBucket(cases, bucketOf) {
    const byBucket = {};
    let breach = 0;
    let warn = 0;
    for (const c of cases) {
        if (!SLA_OWNED_STATUSES.includes(c.status)) continue;
        const band = slaBandOf(c);
        const b = bucketOf(c);
        if (!byBucket[b]) byBucket[b] = { ok: 0, warn: 0, breach: 0 };
        byBucket[b][band] = (byBucket[b][band] || 0) + 1;
        if (band === "breach") breach += 1;
        if (band === "warn") warn += 1;
    }
    return { byBucket, total: { breach, warn } };
}

/** SLA mini-badge for the corner of a tile. Shows nothing if no warn/breach. */
export function TileSLABadge({ warn = 0, breach = 0 }) {
    if (!warn && !breach) return null;
    return (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold" data-testid="tile-sla-badge">
            {breach > 0 && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-200">
                    <ShieldWarning size={9} weight="fill" />
                    {breach}
                </span>
            )}
            {warn > 0 && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                    <Clock size={9} weight="fill" />
                    {warn}
                </span>
            )}
        </span>
    );
}
